// app/src/lib/plaid/reconciler.js — match Plaid transactions against open invoices.
//
// Rules (SPECIFICATION §5.4 US-1.10):
//   - Vendor name fuzzy match (lower-case, alnum-only, Levenshtein ≤ 3) AND
//   - Amount within ±$1.00 (±100 cents) AND
//   - Transaction date in [issuedDate − 1 day, dueDate + 7 days].
//   - Skip pending=true transactions.
//   - Single match → status=CONFIRMED, set plaidTxId, plaidAccountId.
//   - Multiple matches → leave for manual review (alert).
//   - Amount mismatch > $1.00 within window → status=DISPUTED, alert.
//   - status=PAID_LOCAL past dueDate+3 days → status=OVERDUE, alert.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';

const AMOUNT_TOLERANCE_CENTS = 100;
const DATE_BEFORE_ISSUED_DAYS = 1;
const DATE_AFTER_DUE_DAYS = 7;
const OVERDUE_AFTER_DUE_DAYS = 3;

export async function reconcileAll() {
  const claims = await currentClaims();
  if (!claims) return { matched: 0, disputed: 0, overdue: 0 };
  const userId = claims.sub;

  const openInvoices = await db.invoices
    .where('[userId+status]')
    .anyOf([[userId, 'RECEIVED'], [userId, 'PAID_LOCAL']])
    .toArray();
  if (openInvoices.length === 0) return { matched: 0, disputed: 0, overdue: 0 };

  // Index transactions by date proximity. Skip pending.
  const txs = await db.plaidTransactions
    .where('[userId+date]')
    .between([userId, '0000-00-00'], [userId, '9999-99-99'])
    .filter((t) => !t.pending)
    .toArray();

  let matched = 0, disputed = 0, overdue = 0;
  const today = isoToday();

  for (const inv of openInvoices) {
    if (inv.plaidTxId) continue; // already matched.

    const vendor = await db.vendors.get(inv.vendorId);
    // Card-paid vendors used to be skipped ("no did-it-clear signal"). Now that
    // the card feed is synced, we CAN reconcile them: the charge carries the
    // merchant name, so it flows through the fuzzy name-match below. We only
    // auto-confirm a single clean match though — a card mismatch/ambiguity is
    // left silent (no DISPUTED/OVERDUE noise), since cards aren't due-tracked.
    const isCard = vendor?.paymentMethod === 'card';

    const winStart = addDays(inv.issuedDate, -DATE_BEFORE_ISSUED_DAYS);
    const winEnd   = addDays(inv.dueDate || inv.issuedDate, DATE_AFTER_DUE_DAYS);
    const inWindow = txs.filter((t) => t.date >= winStart && t.date <= winEnd);
    const candidates = paymentCandidates(inWindow, vendor, inv);

    if (candidates.length === 0) {
      // Possibly overdue (non-card only — cards aren't due-tracked).
      if (!isCard && inv.status === 'PAID_LOCAL' && inv.dueDate && addDays(inv.dueDate, OVERDUE_AFTER_DUE_DAYS) < today) {
        await db.invoices.put({ ...inv, status: 'OVERDUE' });
        await addAlert(userId, 'warning', 'INVOICE_OVERDUE', `Invoice ${inv.invoiceNumber || ''} appears overdue`);
        overdue++;
      }
      continue;
    }

    const exactCent = candidates.filter((t) => amountMatches(t.amountCents, inv.totalCents));

    if (exactCent.length === 1) {
      const tx = exactCent[0];
      await db.invoices.put({ ...inv, status: 'CONFIRMED', plaidTxId: tx.plaidTxId, plaidAccountId: tx.plaidAccountId });
      matched++;
    } else if (isCard) {
      // Card: only a single clean match auto-confirms. Ambiguous or amount-off
      // card matches are left untouched — no dispute/overdue alerts for cards.
    } else if (exactCent.length === 0 && candidates.length >= 1) {
      // Vendor matched but no amount fit → DISPUTED.
      await db.invoices.put({ ...inv, status: 'DISPUTED' });
      // §12 catalog: invoice_disputed is warning, not critical.
      await addAlert(userId, 'warning', 'INVOICE_AMOUNT_MISMATCH', `Amount mismatch on invoice ${inv.invoiceNumber || ''}`);
      disputed++;
    } else {
      // Multiple candidates — leave for manual selection in /receipts/review.
      await addAlert(userId, 'info', 'INVOICE_MULTI_MATCH', `Multiple candidate payments for invoice ${inv.invoiceNumber || ''}`);
    }
  }

  return { matched, disputed, overdue };
}

/**
 * Pick the bank transactions (already filtered to the invoice's date window)
 * that identify THIS vendor's payment, per the vendor's configured match mode:
 *   - card                         → fuzzy vendor-name match (the card feed
 *     carries the merchant name; reconcileAll only auto-confirms a single
 *     clean amount match and stays silent otherwise)
 *   - paymentMatch 'amount' (or default for check_on_delivery) → amount-anchored
 *     (checks don't carry the vendor name, so match the invoice total ± tolerance)
 *   - paymentMatch 'label' (payment-service descriptor) → bank text contains the
 *     label AND the amount fits
 *   - otherwise → fuzzy vendor-name match (amount applied downstream → DISPUTED on
 *     a name hit with the wrong amount)
 * Pure + exported for testing.
 */
export function paymentCandidates(inWindow, vendor, invoice) {
  const method = vendor?.paymentMethod;
  const amountOk = (t) => amountMatches(t.amountCents, invoice.totalCents);
  const by = vendor?.paymentMatch || (method === 'check_on_delivery' ? 'amount' : null);

  if (by === 'amount') return inWindow.filter(amountOk);
  if (by === 'label' && vendor?.paymentLabel) {
    const lbl = normalize(vendor.paymentLabel);
    if (!lbl) return [];
    return inWindow.filter((t) => (normalize(t.merchantName).includes(lbl) || normalize(t.name || '').includes(lbl)) && amountOk(t));
  }
  const vName = vendor?.name || '';
  return inWindow.filter((t) => fuzzyMatches(vName, t.merchantName || t.name || ''));
}

// A receipt total and its card charge can differ by a small tax / bag-fee /
// rounding gap, so allow max($1, 1% of the invoice), capped at $3.
export function amountMatches(txCents, invoiceCents) {
  const tol = Math.min(300, Math.max(AMOUNT_TOLERANCE_CENTS, Math.round(Math.abs(invoiceCents || 0) * 0.01)));
  return Math.abs((txCents || 0) - (invoiceCents || 0)) <= tol;
}

function normalize(s) {
  return (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

// Generic words that don't identify a merchant. Dropped before token matching
// so "US FOODS | CHEF'S STORE" still matches the card's "CHEFSTORE 7583".
const NAME_STOPWORDS = new Set([
  'the', 'and', 'of', 'inc', 'llc', 'llp', 'ltd', 'co', 'com', 'corp', 'company',
  'us', 'usa', 'foods', 'food', 'store', 'stores', 'market', 'markets', 'mkt',
  'wholesale', 'quality', 'center', 'centers', 'supply', 'supplies', 'group',
  'restaurant', 'services', 'service', 'grocery',
]);

// Distinctive word tokens: lower-cased, punctuation → spaces, drop pure numbers
// (store #s), generic stopwords, and 1–2 char noise.
function nameTokens(s) {
  return (s || '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .filter((t) => t && t.length >= 3 && !/^\d+$/.test(t) && !NAME_STOPWORDS.has(t));
}

function tokenMatch(ta, tb) {
  for (const x of ta) {
    for (const y of tb) {
      if (x === y) return true;
      const [short, long] = x.length <= y.length ? [x, y] : [y, x];
      if (short.length >= 4 && long.includes(short)) return true;    // "chef" ⊂ "chefstore"
      if (short.length >= 5 && levenshtein(x, y) <= 1) return true;  // one-char typo
    }
  }
  return false;
}

// Fuzzy vendor-name match. Takes RAW names (not pre-normalized) so it can do
// both whole-string matching AND token-overlap matching — the latter survives
// store-number suffixes ("QFC #5894") and extra words ("US FOODS | …").
function fuzzyMatches(aRaw, bRaw) {
  const a = normalize(aRaw), b = normalize(bRaw);
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.includes(b) || b.includes(a)) return true;
  if (levenshtein(a, b) <= 3) return true;
  return tokenMatch(nameTokens(aRaw), nameTokens(bRaw));
}

function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = i - 1;
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const tmp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
      prev = tmp;
    }
  }
  return dp[n];
}

function isoToday() { return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10); }

function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

async function addAlert(userId, severity, kind, title) {
  await db.alerts.add({
    alertId: crypto.randomUUID(),
    userId, severity, kind, title,
    createdAt: Date.now(),
    // IndexedDB compound keys do not accept booleans; use 0 / 1 consistently.
    dismissed: 0,
  });
}
